/**
 * AI CONTEXT: mobile-first - Bottom navigation for small screens
 * 
 * Purpose:
 * - Provide quick access to key sections on mobile
 * - Persist cart badge and highlight active route
 * - Maintain glassmorphism and brand styles
 * 
 * Future considerations:
 * - Add haptic feedback (PWA)
 * - Support deep links and notifications
 * - Configurable nav items per role
 */

import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Home, ShoppingBag, Gift, BookOpen, ShoppingCart } from 'lucide-react';
import { t } from '../../i18n';
import { useCart } from '../../contexts/CartContext';

const MobileNav = () => {
  const location = useLocation();
  const { getCartCount } = useCart();

  const navigation = [
    { name: t('nav.home'), href: '/', icon: Home },
    { name: t('nav.store'), href: '/store', icon: ShoppingBag },
    { name: t('nav.giveaways'), href: '/giveaways', icon: Gift },
    { name: t('nav.blog'), href: '/blog', icon: BookOpen },
    { name: t('nav.cart'), href: '/checkout', icon: ShoppingCart },
  ];

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    if (path === '/blog') return location.pathname.startsWith('/blog');
    return location.pathname === path;
  };

  return (
    <nav className="md:hidden fixed bottom-0 left-0 right-0 glass-nav z-40">
      <div className="flex justify-around p-2">
        {navigation.map((item) => {
          const Icon = item.icon;
          return (
            <Link
              key={item.name}
              to={item.href}
              className={`flex flex-col items-center py-2 px-2 flex-1 transition-all duration-300 rounded-xl ${
                isActive(item.href)
                  ? 'text-theme-bronze bg-glass-orange/20 shadow-glow'
                  : 'text-theme-silver hover:text-theme-bronze hover:bg-glass-white/10'
              }`}
            >
              <div className="relative">
                <Icon className="h-6 w-6" />
                {item.name === t('nav.cart') && getCartCount() > 0 && (
                  <span className="absolute -top-1 -right-1 bg-theme-bronze text-white text-xs rounded-full h-4 w-4 flex items-center justify-center shadow-glow">
                    {getCartCount()}
                  </span>
                )}
              </div>
              <span className="text-xs mt-1 font-medium">{item.name}</span>
            </Link>
          );
        })}
      </div>
    </nav>
  );
};

export default MobileNav;
